/* eslint-disable max-lines -- Phase 4: split remaining Trade workspace / modals; tracked in roadmap */
/**
 * 
 * Main page for handling card trades/buys/sells with customers.
 * Features:
 * - Quick search for cards (from catalog) and inventory
 * - Two-sided trade builder (store vs customer items)
 * - Real-time value difference calculation
 * - Payment tracking
 * - Trade completion with inventory updates
 */

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { api, withIdempotencyKey } from '../../utils/api'
import { reportError } from '../../utils/reportError'
import { trackUserAction } from '../../utils/telemetry'
import { useOrgLocale } from '../../hooks/useOrgLocale'
import { isSupportedCurrency, useCurrency } from '../../contexts/CurrencyContext'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../hooks/useSubscription'
import { UnifiedCardSearch, type CardSearchResult, type InventorySearchResult } from '../../components/cards'
import { ReportIssueModal, ActionConfirmModal, type ReportIssueContext } from '../../components/modals'
import { UpgradePrompt } from '../../components/ui/UpgradePrompt'
import { getVariantDirectPriceCents, getVariantPriceCents } from '../../utils/price'
import { addScannedProductToTrade as addScannedProductToTradeRequest } from '../../services/scanToTrade'
import { localStorageKeys } from '../../constants/storage'
import { 
  ArrowLeftRight, 
  Plus, 
  Check, 
  X,
  DollarSign,
  User,
  Package,
  History,
  AlertCircle,
  AlertTriangle,
  QrCode,
  BarChart3,
  Info,
  Smartphone,
  ScanLine,
  Search,
  MoreHorizontal,
  Eye,
} from 'lucide-react'
import { PageHeader } from '../../components/layout'
import { TradeLineRow } from './createTransaction/TradeLineRow'
import { CustomerViewSheet } from './createTransaction/CustomerViewSheet'
import type { LineVariant } from './createTransaction/TradeLineVariantEditor'
import { createPortal } from 'react-dom'
import { lazy, Suspense } from 'react'
import { useDismiss } from '../../hooks/useDismiss'
import styles from './CreateTransaction.module.css'

import type {
  AddingItemSelection,
  TradeType,
  TradeItem,
  CompletedTradeData,
  TradeResponse,
  TradeVariant,
  CustomerSuggestion,
} from './createTransaction/types'
import {
  enrichVariantsForTrade,
  findDefaultVariant,
  activeTradeStorageKey,
  tradePaymentMethodStorageKey,
  formatActiveTradeRefLabel,
  getTradeItemImage,
  tradeItemToAddPayload,
} from './createTransaction/helpers'
import { optimisticQuantityChange, optimisticRemoveItem } from './createTransaction/tradeTotals'
import { LineQuantityQueue } from './createTransaction/lineQuantityQueue'
import { AddItemModal } from './createTransaction/AddItemModal'
import {
  fromTradeApiResponse,
  mapTradeToCompletedData,
  buildCompleteTradePayload,
} from './createTransaction/tradeMappers'
import {
  CUSTOMER_PERCENT_UI_MAX,
  priceNewCustomerLine,
  readLineMarketCents,
  readLinePricing,
  readTradeCustomerPercent,
  readTradeCustomerPercentSource,
  type LinePriceUpdate,
} from './createTransaction/customerPricing'
import {
  initialTradePercent,
  parseTradeRules,
  priceFollowingCustomerLine,
  rulePercentForPaymentMethod,
  type BulkFloorUsd,
  type CustomerPercentSource,
} from '../../utils/tradeRules'
import { PAYMENT_METHODS } from './createTransaction/tradePaymentMethods'
import { createTransactionCopy } from './createTransactionCopy'
import { useCustomerTypeahead, useFilteredCustomerSuggestions } from './createTransaction/useCustomerTypeahead'
import { CustomerPanel } from './createTransaction/CustomerPanel'
import { BasketModal, type BasketPreviewData, type LoadedBasketSummary } from './createTransaction/BasketModal'
import { PaymentModal } from './createTransaction/PaymentModal'
import { QrScannerModal } from './createTransaction/QrScannerModal'
import { TradeReceipt } from './createTransaction/TradeReceipt'
import { PhonePairingModal } from './createTransaction/PhonePairingModal'
import { useTradeAutoRefresh } from './createTransaction/useTradeAutoRefresh'
import { newCustomerLineIds } from './createTransaction/tradeAutoRefresh'
import { usePrintingChecks } from '../Scanner/usePrintingChecks'
import { printingCheckText } from '../Scanner/checkPrinting'

/** Its own chunk: the scanner's camera loop is only downloaded when someone scans. */
const CardScannerPopup = lazy(() => import('../Scanner/CardScanner'))

function isTradeInventorySearchRow(item: CardSearchResult | InventorySearchResult): item is InventorySearchResult {
  return typeof (item as InventorySearchResult).quantity === 'number' && 'cardId' in item
}

function isTradeCatalogCardRow(item: CardSearchResult | InventorySearchResult): item is CardSearchResult {
  return Array.isArray((item as CardSearchResult).variants)
}

/** GET /trades/rules: the store's cash/credit % and bulk floor, with the floor already in USD cents. */
type TradeRulesResponse = {
  tradeRules?: unknown
  bulkFloorUsd?: BulkFloorUsd | null
  floorUnavailableReason?: string | null
}

/** How long to wait before saving the trade's % again after a dropped request or server error. */
const PERCENT_SAVE_RETRY_MS = 3000

/** A % rounded the way the server compares it, or null for "no %". */
const normalizePercent = (value: number | '' | null): number | null =>
  value === '' || value === null || !Number.isFinite(Number(value)) ? null : Math.round(Number(value) * 1000) / 1000

/** The source a saved draft's % has, reading a % saved before sources existed as typed. */
const savedPercentSource = (trade: TradeType | null): CustomerPercentSource =>
  readTradeCustomerPercentSource(trade) ?? (readTradeCustomerPercent(trade) === null ? 'rules' : 'manual')

/** A basket line the server could not load, so staff can tell the customer which card. */
type BasketUnavailableLine = { name: string; requested: number; available: number }

/** Advisory notes the server attaches to a draft trade when stock moved underneath it. */
type TradeStockNotices = {
  _removedItemsNotice?: string
  _stockShortfallNotice?: string
}

/**
 * Fold the server's draft-trade notices into one line for the warning banner.
 *
 * These are heads-ups, not failures — the transaction still works, a quantity just needs
 * adjusting. They previously went through setError, which painted a red "something broke" toast
 * over what is really an advisory, and buried the one case that does block completion.
 */
function tradeStockNotice(trade: TradeStockNotices): string | null {
  const notices = [trade._removedItemsNotice, trade._stockShortfallNotice].filter(
    (n): n is string => typeof n === 'string' && n.length > 0,
  )
  return notices.length > 0 ? notices.join(' ') : null
}

export const CreateTransaction: React.FC = () => {
  const { formatPrice } = useCurrency()
  // The basket as the customer was quoted it (store currency, verbatim); the USD sale-line
  // total, converted, only for a basket with an unpriced line.
  const formatLoadedBasketTotal = (basket: LoadedBasketSummary): string => {
    const storeCurrency = basket.storeCurrency?.toUpperCase()
    return basket.storeTotalCents !== null && isSupportedCurrency(storeCurrency)
      ? formatPrice(basket.storeTotalCents, storeCurrency)
      : formatPrice(basket.totalCents)
  }
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const orgLocale = useOrgLocale()
  const orgId = user?.organizationId
  const { canAccessTradeCenter } = useSubscription()
  const navigate = useNavigate()
  const location = useLocation()

  const [searchParams] = useSearchParams()
  const tradeIdParam = searchParams.get('tradeId')
  const receiptTradeIdParam = searchParams.get('receiptTradeId')
  const quickAddProductId = searchParams.get('quickAdd')
  const sideParam = searchParams.get('side')
  /** On a phone, each side's search is one tap behind Scan. */
  const [phoneSearch, setPhoneSearch] = useState<Record<'store' | 'customer', boolean>>({ store: false, customer: false })
  const [dealMenuOpen, setDealMenuOpen] = useState(false)
  const [showCustomerView, setShowCustomerView] = useState(false)
  const dealMenuRef = useRef<HTMLDivElement>(null)
  useDismiss(dealMenuRef, dealMenuOpen, () => setDealMenuOpen(false))
  
  // Trade state
  const [currentTrade, setCurrentTrade] = useState<TradeType | null>(null)
  /** Image URLs that failed to load — avoids mutating parent DOM from img onError. */
  const [tradeImageLoadFailedIds, setTradeImageLoadFailedIds] = useState<Set<string>>(() => new Set())
  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [notes, setNotes] = useState('')

  const {
    customerSuggestions,
    setCustomerSuggestions,
    showSuggestions,
    customerSearchLoading,
    customerSearchError,
    activeSuggestion,
    activeSuggestionField,
    setActiveSuggestion,
    setActiveSuggestionField,
    setShowSuggestions,
    setCustomerSearchError,
    setCustomerSearchLoading,
    customerNameRef,
    customerEmailRef,
    customerPhoneRef,
    suggestionsRef,
    debounceTimer,
    searchCustomers,
  } = useCustomerTypeahead()

  const filteredCustomerSuggestions = useFilteredCustomerSuggestions(
    activeSuggestionField,
    customerName,
    customerEmail,
    customerPhone,
    customerSuggestions,
  )

  const shouldShowCustomerSuggestionPanel = (field: 'name' | 'email' | 'phone') => {
    const q = field === 'name' ? customerName : field === 'email' ? customerEmail : customerPhone
    return (
      showSuggestions &&
      activeSuggestionField === field &&
      (customerSearchLoading ||
        customerSearchError !== null ||
        filteredCustomerSuggestions.length > 0 ||
        q.trim().length >= 2)
    )
  }

  const handleCustomerFieldChange = (field: 'name' | 'email' | 'phone', value: string) => {
    if (field === 'name') setCustomerName(value)
    if (field === 'email') setCustomerEmail(value)
    if (field === 'phone') setCustomerPhone(value)

    setActiveSuggestionField(field)
    setActiveSuggestion(-1)
    setCustomerSearchError(null)

    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => searchCustomers(value), 250)
  }

  const handleCustomerFieldFocus = (field: 'name' | 'email' | 'phone') => {
    setActiveSuggestionField(field)
    if (customerSuggestions.length > 0) {
      setShowSuggestions(true)
    }
  }

  const handleSuggestionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const list = filteredCustomerSuggestions
    if (!showSuggestions || list.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveSuggestion((prev) => Math.min(prev + 1, list.length - 1))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveSuggestion((prev) => Math.max(prev - 1, 0))
      return
    }
    if (e.key === 'Enter' && activeSuggestion >= 0) {
      e.preventDefault()
      selectCustomerSuggestion(list[activeSuggestion])
      return
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false)
      setCustomerSearchError(null)
    }
  }

  const selectCustomerSuggestion = (suggestion: CustomerSuggestion) => {
    setCustomerName(suggestion.customerName)
    if (suggestion.customerEmail) setCustomerEmail(suggestion.customerEmail)
    if (suggestion.customerPhone) setCustomerPhone(suggestion.customerPhone)
    setShowSuggestions(false)
    setCustomerSuggestions([])
    setCustomerSearchError(null)
    setCustomerSearchLoading(false)
  }

  const getTradeTypeLabel = (type: 'trade' | 'buy' | 'sell') => {
    switch (type) {
      case 'trade':
        return 'Swap'
      case 'buy':
        return 'Buy'
      case 'sell':
        return 'Sell'
      default:
        return type
    }
  }
  
  // Search is now handled by UnifiedCardSearch component
  
  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [completedTradeData, setCompletedTradeData] = useState<CompletedTradeData | null>(null)
  const [receiptNumberLoading, setReceiptNumberLoading] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  // Read on the first render: the save effect below must never see the default and overwrite the
  // method the scanner's Send to trade just chose.
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(() => {
    const raw = orgId ? localStorage.getItem(tradePaymentMethodStorageKey(orgId)) : null
    return raw && PAYMENT_METHODS.some((m) => m.value === raw) ? raw : 'cash'
  })
  const [cancelTradeConfirmOpen, setCancelTradeConfirmOpen] = useState(false)
  const [cancelTradeBusy, setCancelTradeBusy] = useState(false)
  const [undoRemoveStack, setUndoRemoveStack] = useState<TradeItem[]>([])
  
  // Item being added/edited
  const [addingItem, setAddingItem] = useState<AddingItemSelection | null>(null)
  const [itemQuantity, setItemQuantity] = useState(1)
  const [itemValueCents, setItemValueCents] = useState(0)
  const [marketValueCents, setMarketValueCents] = useState(0)
  const [overrideTotalValueCents, setOverrideTotalValueCents] = useState<number | ''>('')
  /** True once the user hand-sets a customer card's price/%, locking it from the global Customer % of Market. */
  const [priceIsOverride, setPriceIsOverride] = useState(false)

  const [bulkCustomerPercent, setBulkCustomerPercent] = useState<number | ''>('')
  /**
   * Where the trade's % came from. 'rules': the store's cash or credit rule for the selected
   * payment method, so changing the method switches it. 'manual': typed for this trade, and a
   * payment-method change leaves it alone. Saved on the draft with the %.
   */
  const [percentSource, setPercentSource] = useState<CustomerPercentSource>('rules')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  // Bumped after a failed save of the %, so the save effect runs again: Complete waits for that
  // save, and a dropped request would otherwise hold it until someone retyped the %.
  const [percentSaveRetry, setPercentSaveRetry] = useState(0)
  const [itemCondition, setItemCondition] = useState('')
  const [itemPrinting, setItemPrinting] = useState('')
  const [selectedVariantId, setSelectedVariantId] = useState<string>('')
  const bulkApplyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bulkApplyRunIdRef = useRef(0)
  const bulkPercentAbortRef = useRef<AbortController | null>(null)

  const [showCustomerDetails, setShowCustomerDetails] = useState(false)
  const [savingCustomerDetails, setSavingCustomerDetails] = useState(false)
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null)
  const quickAddProcessingRef = useRef<string | null>(null)
  const [showQrScanner, setShowQrScanner] = useState(false)
  /** The Scan with phone code is open, for this side. */
  const [phonePairingSide, setPhonePairingSide] = useState<'store' | 'customer' | null>(null)
  /** The scanner open over this trade. */
  const [scanPopup, setScanPopup] = useState<{ tradeId: string; side: 'store' | 'customer' } | null>(null)
  /** The card just moved to the other side, for Undo. */
  const [lastMove, setLastMove] = useState<{ itemId: string; name: string; toSide: 'store' | 'customer'; valueCents: number } | null>(null)
  const [movingItemId, setMovingItemId] = useState<string | null>(null)

  // Allow Escape to dismiss the full-screen image zoom (alongside click-to-close
  // and the explicit close button) so a large image can never lock up the page.
  useEffect(() => {
    if (!zoomedImageUrl) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomedImageUrl(null)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [zoomedImageUrl])

  // Load basket state
  const [showLoadBasketModal, setShowLoadBasketModal] = useState(false)
  const [basketCodeInput, setBasketCodeInput] = useState('')
  const [loadingBasket, setLoadingBasket] = useState(false)
  const [basketPreview, setBasketPreview] = useState<BasketPreviewData | null>(null)
  const [basketLoadError, setBasketLoadError] = useState<string | null>(null)
  const [committingBasket, setCommittingBasket] = useState(false)
  const [loadedBasket, setLoadedBasket] = useState<LoadedBasketSummary | null>(null)
  const currentTradeRef = useRef<TradeType | null>(null)
  const pendingDraftCleanupRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const UNDO_REMOVE_STACK_MAX = 10
  const undoRemoveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [reportModalOpen, setReportModalOpen] = useState(false)
  const [reportContext, setReportContext] = useState<ReportIssueContext | null>(null)
  const savedTradePercent = normalizePercent(readTradeCustomerPercent(currentTrade))

  useEffect(() => {
    if (!orgId) return
    const raw = localStorage.getItem(tradePaymentMethodStorageKey(orgId))
    if (raw && PAYMENT_METHODS.some(m => m.value === raw)) {
      setSelectedPaymentMethod(raw)
    }
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    localStorage.setItem(tradePaymentMethodStorageKey(orgId), selectedPaymentMethod)
  }, [orgId, selectedPaymentMethod])

  // The store's trade rules (Settings → Trade-ins). Until they load, `rulePercent` is undefined
  // and nothing follows them yet.
  const tradeRulesQuery = useQuery({
    queryKey: ['trade-rules', orgId],
    queryFn: async () => ((await api.get('/trades/rules')).data?.data ?? {}) as TradeRulesResponse,
    enabled: Boolean(orgId),
    staleTime: 60_000,
  })
  const tradeRules = useMemo(
    () => (tradeRulesQuery.data ? parseTradeRules(tradeRulesQuery.data.tradeRules, 'USD') : null),
    [tradeRulesQuery.data],
  )
  const bulkFloorUsd = tradeRulesQuery.data?.bulkFloorUsd ?? null
  const rulePercent: number | null | undefined = tradeRules
    ? rulePercentForPaymentMethod(tradeRules, selectedPaymentMethod)
    : undefined
  const rulePercentRef = useRef(rulePercent)
  rulePercentRef.current = rulePercent
  const bulkFloorUsdRef = useRef<BulkFloorUsd | null>(bulkFloorUsd)
  bulkFloorUsdRef.current = bulkFloorUsd

  // A % that follows the rules tracks them: a new draft starts from the rule for its payment
  // method, and switching cash ↔ store credit switches the %, which the effect further down saves
  // and uses to reprice every line that follows it. A typed % is never overwritten here.
  useEffect(() => {
    if (percentSource !== 'rules' || rulePercent === undefined) return
    const next = rulePercent === null ? '' : Math.min(CUSTOMER_PERCENT_UI_MAX, rulePercent)
    setBulkCustomerPercent((prev) => (prev === next ? prev : next))
  }, [percentSource, rulePercent])

  // A trade that ends (completed, cancelled, deleted) hands the next one back to the store rule,
  // so a % typed for one customer doesn't carry over. With no rule for the payment method the
  // typed % stays, as it always did.
  const lastTradeIdRef = useRef<string | null>(null)
  useEffect(() => {
    const id = currentTrade?.id ?? null
    if (lastTradeIdRef.current && !id && typeof rulePercentRef.current === 'number') setPercentSource('rules')
    lastTradeIdRef.current = id
  }, [currentTrade?.id])

  /** Show a loaded draft's % (and where it came from) instead of the page's current one. */
  const restoreTradePercent = useCallback((trade: TradeType) => {
    const initial = initialTradePercent(trade.metadata, rulePercentRef.current)
    setPercentSource(initial.source)
    setBulkCustomerPercent(initial.percent === null ? '' : Math.min(CUSTOMER_PERCENT_UI_MAX, initial.percent))
  }, [])

  const hasCustomerInfo = (name?: string, email?: string, phone?: string) =>
    Boolean(name?.trim() || email?.trim() || phone?.trim())

  const basketCodePrefix = (() => {
    const orgName = user?.organizationName?.trim()
    if (!orgName) return ''
    const letters = orgName.toUpperCase().replace(/[^A-Z]/g, '')
    const prefix = letters.slice(0, 3).padEnd(3, 'X')
    return `${prefix}-`
  })()

  const normalizeBasketCode = useCallback((value: string) => {
    const raw = value.trim().toUpperCase()
    if (!raw) return basketCodePrefix
    if (!basketCodePrefix) return raw
    if (raw.includes('-')) return raw
    return `${basketCodePrefix}${raw}`
  }, [basketCodePrefix])

  const customerSummary = [currentTrade?.customerName || customerName, currentTrade?.customerEmail || customerEmail, currentTrade?.customerPhone || customerPhone]
    .filter(Boolean)
    .join(' · ')

  const hasFilledCustomerInfo = hasCustomerInfo(
    currentTrade?.customerName || customerName,
    currentTrade?.customerEmail || customerEmail,
    currentTrade?.customerPhone || customerPhone,
  )

  /** Latest guard for unmount cleanup — ref avoids stale closures from an effect with `[]` deps. */
  const shouldDeleteDraftTradeRef = useRef<(trade: TradeType | null) => boolean>(() => false)
  shouldDeleteDraftTradeRef.current = (trade: TradeType | null) => {
    if (!trade || trade.status !== 'draft') return false

    const hasItems = (trade.storeItems?.length || 0) > 0 || (trade.customerItems?.length || 0) > 0
    const hasCustomer =
      hasCustomerInfo(trade.customerName, trade.customerEmail, trade.customerPhone) ||
      hasCustomerInfo(customerName, customerEmail, customerPhone)
    const hasNotes = Boolean(trade.notes?.trim() || notes.trim())

    return !hasItems && !hasCustomer && !hasNotes
  }

  const addScannedProductToTrade = useCallback(
    async (
      productId: string,
      options?: { preferredTrade?: TradeType | null; suppressNavigation?: boolean; allowCreateTrade?: boolean },
    ) => {
      setLoading(true)
      setError(null)

      try {
        const result = await addScannedProductToTradeRequest(
          productId,
          options,
          {
            getTargetTrade: () => currentTradeRef.current,
            setCurrentTrade,
            navigate,
            organizationId: user?.organizationId,
          },
        )
        setSuccess('Scanned item added to transaction')
        setTimeout(() => setSuccess(null), 1500)
        return result
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
        const message =
          anyErr?.response?.data?.message ||
          anyErr?.response?.data?.error ||
          anyErr?.message ||
          'Failed to add scanned item to transaction'
        reportError(err, { page: 'CreateTransaction', action: 'addScannedProductToTrade' })
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [navigate, user?.organizationId],
  )

  useEffect(() => {
    const loadReceiptTrade = async () => {
      if (!receiptTradeIdParam) return

      setLoading(true)
      setError(null)
      try {
        const response = await api.get(`/trades/${receiptTradeIdParam}`)
        const trade = response.data?.data as TradeResponse | undefined
        if (!trade) {
          setError('Transaction not found')
          return
        }
        if (trade.status !== 'completed') {
          setError('Receipt is only available for completed transactions')
          return
        }

        setCurrentTrade(null)
        setCompletedTradeData(mapTradeToCompletedData(trade))
      } catch (err: any) {
        reportError(err, { page: 'CreateTransaction', action: 'loadReceiptTrade' })
        setError(err.response?.data?.message || 'Failed to load receipt')
      } finally {
        setLoading(false)
      }
    }

    void loadReceiptTrade()
  }, [mapTradeToCompletedData, receiptTradeIdParam])

  useEffect(() => {
    if (currentTrade?.id && orgId) {
      localStorage.setItem(activeTradeStorageKey(orgId), currentTrade.id)
    }
  }, [currentTrade?.id, orgId])

  useEffect(() => {
    currentTradeRef.current = currentTrade
  }, [currentTrade])

  // Quantity +/- edits go through one queue per page (lineQuantityQueue.ts): sent in order, the
  // newest value per line winning, and the server's trade applied only once every edit has been
  // answered — so a slow reply can never overwrite a newer quantity, and Complete waits for them.
  const [qtyEditsPending, setQtyEditsPending] = useState(false)
  const qtyQueueRef = useRef<LineQuantityQueue<(TradeType & TradeStockNotices) | null> | null>(null)
  const qtyQueueTradeIdRef = useRef<string | null>(null)
  if (!qtyQueueRef.current) {
    qtyQueueRef.current = new LineQuantityQueue<(TradeType & TradeStockNotices) | null>({
      send: async (itemId, quantityChange) => {
        const tradeId = qtyQueueTradeIdRef.current
        if (!tradeId) return null
        const response = await api.patch(`/trades/${tradeId}/items/${itemId}`, { quantityChange })
        return (response.data?.data?.trade ?? response.data?.trade ?? null) as (TradeType & TradeStockNotices) | null
      },
      onPendingChange: setQtyEditsPending,
      onDrained: ({ last, failed }) => {
        const tradeId = qtyQueueTradeIdRef.current
        if (!tradeId || currentTradeRef.current?.id !== tradeId) return
        // The last answer is only the whole truth if no line was added or removed while the
        // edits were in flight (a barcode scan mid-burst): its item list would predate that
        // line, drop it from the screen though it is on the receipt, and invite a second scan.
        const sameLines = (answer: { items?: Array<{ id?: unknown }> }) => {
          const onScreen = new Set((currentTradeRef.current?.items ?? []).map((i: { id?: unknown }) => String(i.id)))
          const answered = (answer.items ?? []).map((i) => String(i.id))
          return answered.length === onScreen.size && answered.every((id) => onScreen.has(id))
        }
        if (failed.length === 0 && last && Array.isArray(last.items) && sameLines(last)) {
          // Re-read the notice from every server answer, so lowering a quantity to fit the
          // remaining stock clears the warning instead of leaving a stale one on screen.
          setWarning(tradeStockNotice(last))
          setCurrentTrade(fromTradeApiResponse(last))
          return
        }
        if (failed.length > 0) {
          const err = failed[failed.length - 1].error as { response?: { data?: { message?: string } } }
          reportError(failed[failed.length - 1].error, { page: 'CreateTransaction', action: 'updateItem' })
          setError(err?.response?.data?.message || 'Failed to update item')
        }
        // A refused edit, an answer without lines, or lines that changed mid-burst leaves the screen guessing: show the
        // server's trade as it stands.
        void (async () => {
          try {
            const refreshed = await api.get(`/trades/${tradeId}`)
            const t = refreshed.data?.data as (TradeType & TradeStockNotices) | undefined
            if (t && currentTradeRef.current?.id === tradeId && !qtyQueueRef.current?.pending) {
              setCurrentTrade(fromTradeApiResponse(t))
              setWarning(tradeStockNotice(t))
            }
          } catch (refetchErr: unknown) {
            reportError(refetchErr, { page: 'CreateTransaction', action: 'refetchAfterQuantityEdit' })
            setError('Could not verify the quantity change. Refresh before completing.')
          }
        })()
      },
    })
  }

  // A different trade on screen: edits queued for the old one must not land on it.
  useEffect(() => {
    const tradeId = currentTrade?.id ?? null
    if (qtyQueueTradeIdRef.current !== tradeId) {
      qtyQueueRef.current?.reset()
      qtyQueueTradeIdRef.current = tradeId
    }
  }, [currentTrade?.id])

  useEffect(() => {
    const loadExistingTrade = async () => {
      if (!tradeIdParam || receiptTradeIdParam) return

      setLoading(true)
      setError(null)
      try {
        const response = await api.get(`/trades/${tradeIdParam}`)
        const trade = response.data?.data as (TradeType & TradeStockNotices) | undefined
        if (!trade) {
          setError('Transaction not found')
          return
        }

        setCurrentTrade(fromTradeApiResponse(trade))
        // The trade's Customer % of Market lives on the draft, so a reload or another till picks it up.
        restoreTradePercent(trade)
        setWarning(tradeStockNotice(trade))
        setCustomerName(trade.customerName || '')
        setCustomerEmail(trade.customerEmail || '')
        setCustomerPhone(trade.customerPhone || '')
        setNotes(trade.notes || '')
      } catch (err: any) {
        reportError(err, { page: 'CreateTransaction', action: 'loadExistingTrade' })
        setError(err.response?.data?.message || 'Failed to load transaction')
      } finally {
        setLoading(false)
      }
    }

    // Only auto-load if we're not already in a trade
    if (!currentTrade) {
      void loadExistingTrade()
    }
  }, [tradeIdParam, receiptTradeIdParam, currentTrade, restoreTradePercent])
  
  // All search functionality is now handled by UnifiedCardSearch component

  // Lazily create the draft trade.
  // We deliberately avoid creating an empty transaction on mount — a DB row is
  // only created the first time the user does something real (adds an item,
  // saves customer info, loads a basket, or scans a card). This keeps abandoned
  // "I just opened the page" sessions out of Transaction History entirely.
  // Throws on failure so callers can surface their own error message.
  const ensureTrade = useCallback(async (): Promise<TradeType> => {
    const existing = currentTradeRef.current
    if (existing && existing.status === 'draft') return existing

    const response = await api.post('/trades', {
      type: 'trade',
      customerName: customerName.trim() || undefined,
      customerEmail: customerEmail.trim() || undefined,
      customerPhone: customerPhone.trim() || undefined,
      notes: notes.trim() || undefined,
    })

    const trade = response.data.data as TradeType
    const normalizedTrade = fromTradeApiResponse(trade)
    // Update the ref synchronously so two quick first-actions don't both create.
    currentTradeRef.current = normalizedTrade
    setCurrentTrade(normalizedTrade)
    if (orgId) {
      localStorage.setItem(activeTradeStorageKey(orgId), normalizedTrade.id)
    }
    return normalizedTrade
  }, [customerName, customerEmail, customerPhone, notes, orgId])

  // "Scan with phone" needs a real draft for the phone to feed; the latest ensureTrade, stable for the modal.
  const ensureTradeRef = useRef(ensureTrade)
  ensureTradeRef.current = ensureTrade
  const ensurePairingTradeId = useCallback(async () => (await ensureTradeRef.current()).id, [])

  useEffect(() => {
    if (receiptTradeIdParam || tradeIdParam || completedTradeData || currentTrade || loading) return
    if (!orgId) return

    const tryResumeActiveTrade = async () => {
      try {
        let activeId = localStorage.getItem(activeTradeStorageKey(orgId))?.trim()
        if (!activeId) {
          const legacy = localStorage.getItem(localStorageKeys.legacyActiveTradeId)?.trim()
          if (legacy) {
            activeId = legacy
            localStorage.setItem(activeTradeStorageKey(orgId), legacy)
            localStorage.removeItem(localStorageKeys.legacyActiveTradeId)
          }
        }
        if (activeId && activeId.length > 0) {
          const res = await api.get(`/trades/${activeId}`)
          const trade = res.data?.data as (TradeType & TradeStockNotices) | undefined
          if (trade && trade.status === 'draft') {
            setCurrentTrade(fromTradeApiResponse(trade))
            restoreTradePercent(trade)
            setWarning(tradeStockNotice(trade))
            setCustomerName(trade.customerName || '')
            setCustomerEmail(trade.customerEmail || '')
            setCustomerPhone(trade.customerPhone || '')
            setNotes(trade.notes || '')
            return
          }
        }
      } catch {
        // ignore — no resumable draft; fall through to the empty builder
      }

      // Cold-start "quick add" (e.g. arriving from an inventory scan): adding a
      // card IS a real action, so create the trade now and attach the product.
      if (quickAddProductId && !receiptTradeIdParam) {
        try {
          const trade = await ensureTrade()
          quickAddProcessingRef.current = `${quickAddProductId}:${trade.id}`
          await addScannedProductToTrade(quickAddProductId, {
            preferredTrade: trade,
            allowCreateTrade: false,
          })
        } catch {
          // error state already surfaced by addScannedProductToTrade
        }
      }
      // Otherwise leave currentTrade null and render the empty in-memory builder —
      // no DB row is created until the user actually enters something.
    }

    void tryResumeActiveTrade()
  }, [receiptTradeIdParam, tradeIdParam, completedTradeData, currentTrade, loading, orgId, quickAddProductId, ensureTrade, addScannedProductToTrade, restoreTradePercent])

  const saveCustomerDetails = async () => {
    setSavingCustomerDetails(true)
    setError(null)
    try {
      // Saving customer info is a real action — create the trade if needed.
      const trade = await ensureTrade()
      const response = await api.patch(`/trades/${trade.id}/customer`, {
        customerName: customerName.trim() || undefined,
        customerEmail: customerEmail.trim() || undefined,
        customerPhone: customerPhone.trim() || undefined,
        notes: notes.trim() || undefined,
      })

      const updated = response.data?.data as TradeType | undefined
      if (updated) {
        setCurrentTrade(fromTradeApiResponse(updated))
        setSuccess('Customer info updated')
        setTimeout(() => setSuccess(null), 2000)
      }
    } catch (err: any) {
      const apiMessage = err.response?.data?.message || err.response?.data?.error
      reportError(err, { page: 'CreateTransaction', action: 'saveCustomerDetails' })
      setError(apiMessage || 'Failed to update customer info')
    } finally {
      setSavingCustomerDetails(false)
    }
  }

  useEffect(() => {
    const processQuickAdd = async () => {
      if (!quickAddProductId || receiptTradeIdParam) return

      if (!currentTrade) {
        return
      }

      const dedupeKey = `${quickAddProductId}:${currentTrade.id}`
      if (quickAddProcessingRef.current === dedupeKey) return
      quickAddProcessingRef.current = dedupeKey

      try {
        await addScannedProductToTrade(quickAddProductId, { allowCreateTrade: false })
      } catch {
        // Error state is already handled in addScannedProductToTrade
      }
    }

    void processQuickAdd()
  }, [quickAddProductId, receiptTradeIdParam, currentTrade?.id, addScannedProductToTrade])

  // ── Basket loading ────────────────────────────────────────────────────

  const handlePreviewBasket = useCallback(async () => {
    const code = normalizeBasketCode(basketCodeInput)
    if (!code) return
    setBasketCodeInput(code)
    setLoadingBasket(true)
    setBasketLoadError(null)
    setBasketPreview(null)
    try {
      const res = await api.get(`/basket/${encodeURIComponent(code)}`)
      const payload = res.data
      if (!payload?.success) {
        throw new Error(payload?.error ?? 'No basket matches that code. Check the code with the customer and try again.')
      }
      setBasketPreview(payload.data)
    } catch (err: unknown) {
      reportError(err, { page: 'CreateTransaction', action: 'loadBasket' })
      const anyErr = err as { response?: { data?: { error?: string; message?: string } }; message?: string }
      setBasketLoadError(
        anyErr?.response?.data?.message ??
        anyErr?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Could not load basket')
      )
    } finally {
      setLoadingBasket(false)
    }
  }, [basketCodeInput, normalizeBasketCode])

  const closeLoadBasketModal = useCallback(() => {
    setShowLoadBasketModal(false)
    setBasketLoadError(null)
    setBasketPreview(null)
    setBasketCodeInput(basketCodePrefix)
  }, [basketCodePrefix])

  useEffect(() => {
    if (!showLoadBasketModal) return
    if (basketCodeInput.trim()) return
    if (!basketCodePrefix) return
    setBasketCodeInput(basketCodePrefix)
  }, [basketCodeInput, basketCodePrefix, showLoadBasketModal])

  const handleCommitBasket = useCallback(async () => {
    if (!basketPreview) return
    if (loadedBasket && loadedBasket.code !== basketPreview.code) {
      setBasketLoadError(`Basket ${loadedBasket.code} is already linked to this transaction. Remove it before loading another basket.`)
      return
    }
    setCommittingBasket(true)
    setBasketLoadError(null)
    try {
      // Read `currentTradeRef.current` (not `currentTrade` in deps) so we always attach the basket
      // to the latest in-memory draft without recreating this callback on every trade update.
      let targetTrade =
        currentTradeRef.current && currentTradeRef.current.status === 'draft' ? currentTradeRef.current : null
      if (!targetTrade) {
        const createRes = await api.post('/trades', { type: 'trade' })
        const created = createRes.data?.data as TradeType | undefined
        if (!created) throw new Error('Failed to create transaction')
        targetTrade = fromTradeApiResponse(created)
        setCurrentTrade(targetTrade)
      }

      if (!targetTrade) {
        throw new Error('No draft trade available for basket')
      }

      // One atomic call: the server claims the basket and inserts its items in a single
      // transaction. This used to be add-items followed by a fire-and-forget DELETE, so a
      // failed delete or a closed tab left the basket loadable again and a second staff member
      // could duplicate the customer's whole order.
      const response = await api.post(`/trades/${targetTrade.id}/load-basket`, {
        basketCode: basketPreview.code,
      })
      const payload = response.data?.data as
        | { addedCount: number; unavailable: BasketUnavailableLine[]; trade?: TradeType }
        | undefined

      const updatedTrade = payload?.trade
      if (updatedTrade) setCurrentTrade(fromTradeApiResponse(updatedTrade))

      const addedCount = payload?.addedCount ?? 0
      const unavailable = payload?.unavailable ?? []

      const loadedProductIds = new Set(
        basketPreview.items
          .filter((item) => !unavailable.some((miss) => miss.name === item.cardName))
          .map((item) => item.productId),
      )
      const items = basketPreview.items.filter((item) => loadedProductIds.has(item.productId))

      const loadedTotalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0)
      // What the customer was quoted: the listed store-currency prices, summed as given.
      const loadedStoreTotalCents = items.every((item) => typeof item.storePriceCents === 'number')
        ? items.reduce((sum, item) => sum + (item.storePriceCents ?? 0) * item.quantity, 0)
        : null

      const customerSummary = [basketPreview.customerName, basketPreview.customerEmail, basketPreview.customerPhone]
        .filter(Boolean)
        .join(' · ') || null

      setLoadedBasket({
        code: basketPreview.code,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        totalCents: loadedTotalCents,
        storeTotalCents: loadedStoreTotalCents,
        storeCurrency: basketPreview.storeCurrency ?? null,
        customerSummary,
      })

      // Auto-fill customer info if the basket has contact details
      if (basketPreview.customerName || basketPreview.customerEmail || basketPreview.customerPhone) {
        const name  = basketPreview.customerName  ?? ''
        const email = basketPreview.customerEmail ?? ''
        const phone = basketPreview.customerPhone ?? ''
        setCustomerName(name)
        setCustomerEmail(email)
        setCustomerPhone(phone)
        try {
          await api.patch(`/trades/${targetTrade.id}/customer`, {
            customerName: name  || undefined,
            customerEmail: email || undefined,
            customerPhone: phone || undefined,
          })
        } catch { /* non-fatal — customer info is already in local state */ }
      }

      const refreshedTradeRes = await api.get(`/trades/${targetTrade.id}`)
      const refreshedTrade = refreshedTradeRes.data?.data as (TradeType & TradeStockNotices) | undefined
      if (refreshedTrade) {
        setCurrentTrade(fromTradeApiResponse(refreshedTrade))
        // A basket built minutes ago can already be out of date by the time staff load it.
        setWarning(tradeStockNotice(refreshedTrade))
      }

      closeLoadBasketModal()
      setBasketCodeInput(basketCodePrefix)
      trackUserAction('basket_loaded', {
        itemCount: addedCount,
        unavailableCount: unavailable.length,
        basketCode: basketPreview.code,
      })
      // Out-of-stock lines used to vanish without a word, so staff had no idea a card the
      // customer expected was missing until the customer asked. Name them.
      const shortfallNote = unavailable.length > 0
        ? ` ${unavailable.length} item${unavailable.length !== 1 ? 's' : ''} could not be loaded: ` +
          unavailable
            .map((miss) => `${miss.name} (wanted ${miss.requested}, ${miss.available} in stock)`)
            .join('; ')
        : ''

      setSuccess(
        `Loaded ${addedCount} item${addedCount !== 1 ? 's' : ''} from basket ${basketPreview.code}.${shortfallNote}`
      )
    } catch (err: unknown) {
      reportError(err, { page: 'CreateTransaction', action: 'commitBasketToTrade' })
      const anyErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
      setBasketLoadError(
        anyErr?.response?.data?.message ??
        anyErr?.response?.data?.error ??
        (err instanceof Error ? err.message : 'Failed to load basket items')
      )
    } finally {
      setCommittingBasket(false)
    }
  }, [basketCodePrefix, basketPreview, closeLoadBasketModal, loadedBasket])

  const removeLoadedBasket = useCallback(async () => {
    const trade = currentTradeRef.current
    if (!trade || !loadedBasket) return

    const itemIds = (trade.storeItems || [])
      .filter((item) => item.metadata?.basketCode === loadedBasket.code)
      .map((item) => item.id)

    if (itemIds.length === 0) {
      setLoadedBasket(null)
      return
    }

    const basketCodeSnapshot = loadedBasket.code
    setLoading(true)
    setError(null)

    try {
      const response = await api.delete(`/trades/${trade.id}/items/bulk`, {
        data: { itemIds },
      })
      const updatedTrade = response.data?.data?.trade as TradeType | undefined
      if (updatedTrade) setCurrentTrade(fromTradeApiResponse(updatedTrade))

      // Hand the basket back so a mis-load can be redone. Best-effort: the items are already
      // gone, and a failed release must not read as a failed removal.
      await api
        .delete(`/trades/${trade.id}/basket/${encodeURIComponent(basketCodeSnapshot)}`)
        .catch(() => undefined)

      setSuccess(`Removed basket ${basketCodeSnapshot} from store items — the basket can be loaded again`)
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'removeBasketItems' })
      setError(err?.response?.data?.message || 'Failed to remove basket items')
    } finally {
      setLoadedBasket(null)
      setLoading(false)
    }
  }, [loadedBasket])

  // Select a store item to add
  const selectStoreItem = (item: CardSearchResult | InventorySearchResult) => {
    if (isTradeInventorySearchRow(item)) {
      // `priceCents` is the engine's listed USD price (sale, floor, manual expiry, markup), the
      // same figure a QR scan of this card rings up — not the raw priceCents column.
      setAddingItem({ side: 'store', product: item })
      setItemQuantity(1)
      setItemValueCents(item.priceUnavailable ? 0 : item.priceCents)
      setMarketValueCents(item.priceUnavailable ? 0 : item.priceCents)
      if (item.priceUnavailable) setError('This card has no price right now — enter the value manually.')
      setOverrideTotalValueCents('')
      setPriceIsOverride(false)
      setItemCondition(item.condition || '')
      setItemPrinting(item.printing || '')
      setSelectedVariantId('')
      return
    }

    if (!isTradeCatalogCardRow(item)) {
      reportError(new Error('selectStoreItem: unexpected search row shape'), {
        page: 'CreateTransaction',
        action: 'selectStoreItem',
      })
      setError('That search result cannot be added as a store item.')
      return
    }

    // Fallback to catalog card if inventory search is unavailable
    // Enrich variants with all standard conditions
    const enriched = enrichVariantsForTrade((item.variants || []) as TradeVariant[])
    const enrichedCard = { ...item, variants: enriched }
    setAddingItem({ side: 'store', card: enrichedCard })
    setItemQuantity(1)

    setOverrideTotalValueCents('')
    setPriceIsOverride(false)

    if (enriched.length > 0) {
      const bestVariant = findDefaultVariant(enriched)
      if (bestVariant) {
        setSelectedVariantId(bestVariant.id)
        const market = getVariantPriceCents(bestVariant)
        const line = getVariantDirectPriceCents(bestVariant)
        setItemValueCents(line)
        setMarketValueCents(market)
        setItemCondition(bestVariant.condition || '')
        setItemPrinting(bestVariant.printing || '')
      } else {
        setSelectedVariantId('')
        setItemValueCents(item.priceCents && item.priceCents > 0 ? item.priceCents : 0)
        setMarketValueCents(item.priceCents && item.priceCents > 0 ? item.priceCents : 0)
        setItemCondition('')
        setItemPrinting('')
      }
    } else {
      setSelectedVariantId('')
      setItemValueCents(item.priceCents && item.priceCents > 0 ? item.priceCents : 0)
      setMarketValueCents(item.priceCents && item.priceCents > 0 ? item.priceCents : 0)
      setItemCondition('')
      setItemPrinting('')
    }
  }

  // Select a customer card to add
  // Select a customer card to add (catalog). Inventory rows route to store-item flow.
  const selectCustomerCard = (item: CardSearchResult | InventorySearchResult) => {
    if (isTradeInventorySearchRow(item)) {
      selectStoreItem(item)
      return
    }

    if (!isTradeCatalogCardRow(item)) {
      reportError(new Error('selectCustomerCard: non-catalog row'), {
        page: 'CreateTransaction',
        action: 'selectCustomerCard',
      })
      setError('That result cannot be added on the customer side. Add it as a store item instead.')
      return
    }

    const card = item
    // Enrich variants with all standard conditions
    const enriched = enrichVariantsForTrade((card.variants || []) as TradeVariant[])
    const enrichedCard = { ...card, variants: enriched }
    setAddingItem({ side: 'customer', card: enrichedCard })
    setItemQuantity(1)

    setOverrideTotalValueCents('')
    setPriceIsOverride(false)

    const bestVariant = enriched.length > 0 ? findDefaultVariant(enriched) : undefined
    const cardPriceCents = card.priceCents && card.priceCents > 0 ? card.priceCents : 0
    const market = bestVariant ? getVariantPriceCents(bestVariant) : cardPriceCents
    const line = bestVariant ? getVariantDirectPriceCents(bestVariant) : cardPriceCents
    setSelectedVariantId(bestVariant?.id ?? '')
    setItemCondition(bestVariant?.condition || '')
    setItemPrinting(bestVariant?.printing || '')
    setMarketValueCents(market)
    // A new card follows the trade's Customer % of Market, so the modal opens on the value it will
    // actually be added at — it used to open (and add) at 100% of market.
    setItemValueCents(priceNewCustomerLine({
      suggestedValueCents: line,
      marketValueCents: market,
      globalPercent: bulkCustomerPercent === '' ? null : bulkCustomerPercent,
      floor: bulkFloorUsd,
    }).valueCents)
  }

  useEffect(() => {
    const state = location.state as { addCustomerCard?: CardSearchResult; addTradeSide?: 'store' | 'customer' } | null
    if (!state?.addCustomerCard) return

    if (state.addTradeSide === 'store') {
      selectStoreItem(state.addCustomerCard)
    } else {
      selectCustomerCard(state.addCustomerCard)
    }

    navigate(location.pathname + location.search, { replace: true, state: null })
  }, [location.pathname, location.search, location.state, navigate])

  // Add item to trade
  const addItemToTrade = async () => {
    if (!addingItem) return

    const { side, card, product } = addingItem
    
    if (itemValueCents <= 0) {
      setError('Please enter a value for this item')
      return
    }
    
    if (side === 'store' && product && itemQuantity > product.quantity) {
      setError(`Only ${product.quantity} available in inventory`)
      return
    }

    if (side === 'customer' && !card) {
      setError('No card selected')
      return
    }
    
    setLoading(true)
    setError(null)

    try {
      // Lazily create the trade on first item — no empty drafts.
      const trade = await ensureTrade()

      const marketPriceCents = marketValueCents || (typeof (side === 'store' ? product?.priceCents : card?.priceCents) === 'number'
        ? (side === 'store' ? product?.priceCents : card?.priceCents)!
        : 0)

      // A customer card the user didn't hand-price follows the trade's Customer % of Market and
      // says so in its pricing; the server prices it (% or bulk floor) and the line shows what it
      // stored. A hand-set price is locked (manualOverride) and kept as typed.
      const customerLine = side === 'customer'
        ? priceNewCustomerLine({
          suggestedValueCents: itemValueCents,
          marketValueCents: marketPriceCents,
          globalPercent: bulkCustomerPercent === '' ? null : bulkCustomerPercent,
          manualOverride: priceIsOverride,
          manualPercent: null,
          floor: bulkFloorUsd,
        })
        : null

      const itemData = side === 'store' && product ? {
        productId: product.id,
        cardId: product.cardId,
        itemName: product.cardName,
        setName: product.setName,
        setCode: undefined,
        cardNumber: product.setNumber,
        condition: product.condition,
        printing: product.printing,
        quantity: Math.min(itemQuantity, product.quantity),
        valueCents: Math.round(itemValueCents),
        imageUrl: product.imageUrl || null,
        side,
      } : card ? {
        cardId: card.id,
        itemName: card.name,
        setName: card.setName,
        setCode: card.setCode,
        cardNumber: card.setNumber,
        condition: itemCondition,
        printing: itemPrinting,
        quantity: itemQuantity,
        valueCents: customerLine ? customerLine.valueCents : Math.round(itemValueCents),
        metadata: customerLine ? { pricing: customerLine.pricing } : undefined,
        imageUrl: card.imageUrl || null,
        side,
      } : null
      
      const response = await api.post(`/trades/${trade.id}/items`, {
        items: [itemData]
      })
      const pricingNotice: unknown = response.data?.data?.pricingNotice
      if (typeof pricingNotice === 'string' && pricingNotice) setWarning(pricingNotice)
      const updatedTrade: TradeType | null = response.data?.data?.trade || null
      if (updatedTrade) {
        const items = updatedTrade.items || []
        setCurrentTrade({
          ...updatedTrade,
          items: items,
          storeItems: items.filter((item: TradeItem) => item.side === 'store'),
          customerItems: items.filter((item: TradeItem) => item.side === 'customer'),
        })
      }
      setAddingItem(null)
      setSuccess('Item added!')
      setTimeout(() => setSuccess(null), 1500)
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error
      reportError(err, { page: 'CreateTransaction', action: 'addItem' })
      setError(apiMessage || 'Failed to add item')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Save the trade's Customer % of Market (null clears it) and reprice the customer lines that
   * follow it. The % is stored on the draft, so it is sent whenever it differs from the saved one
   * even if no line needs repricing.
   */
  const applyCustomerPercentToAll = useCallback(async (
    pct: number | null,
    source: CustomerPercentSource,
    signal?: AbortSignal,
  ): Promise<boolean> => {
    const trade = currentTradeRef.current
    if (!trade) return false

    const customerItems = trade.customerItems || []
    const clamped = pct === null ? null : Math.max(0, Math.min(1000, pct))
    const normalizedNextPct = normalizePercent(clamped)
    const needsSave = normalizePercent(readTradeCustomerPercent(trade)) !== normalizedNextPct
      || savedPercentSource(trade) !== source

    let needsReprice = false
    for (const item of clamped === null ? [] : customerItems) {
      const existingPricing = readLinePricing(item.metadata)

      // Mirror the server: hand-priced overrides are never touched by the global %.
      if (existingPricing.manualOverride === true) continue

      const marketCents = readLineMarketCents(item.metadata)
      if (marketCents <= 0) continue

      // The same pricing the server applies (bulk floor included), only to tell whether to ask.
      const next = priceFollowingCustomerLine(marketCents, clamped ?? 0, bulkFloorUsdRef.current)
      const currentPct = typeof existingPricing.percentOfMarket === 'number'
        ? Math.round(existingPricing.percentOfMarket * 1000) / 1000
        : null

      if (
        item.valueCents !== next.unitValueCents
        || currentPct !== normalizedNextPct
        || (existingPricing.floorApplied === true) !== next.floorApplied
      ) {
        needsReprice = true
        break
      }
    }

    if (!needsReprice && !needsSave) return false

    const patched = await api.patch(`/trades/${trade.id}/items/bulk-percent`, { percent: clamped, source }, { signal })
    const pricingNotice: unknown = patched.data?.data?.pricingNotice
    if (typeof pricingNotice === 'string' && pricingNotice) setWarning(pricingNotice)

    if (!needsReprice) {
      // Only the saved % changed: patch it in rather than re-reading the trade, so a line added
      // while this was in flight is not replaced by an older copy of the trade.
      setCurrentTrade((prev) => (prev && prev.id === trade.id
        ? { ...prev, metadata: { ...(prev.metadata ?? {}), customerPercentOfMarket: clamped, customerPercentSource: source } }
        : prev))
      return true
    }

    const refreshed = await api.get(`/trades/${trade.id}`, { signal })
    const refreshedTrade = refreshed.data?.data as TradeType | undefined
    if (refreshedTrade) {
      const items = refreshedTrade.items || []
      setCurrentTrade({
        ...refreshedTrade,
        items,
        storeItems: items.filter((i: TradeItem) => i.side === 'store'),
        customerItems: items.filter((i: TradeItem) => i.side === 'customer'),
      })
    }
    return true
  }, [])

  // Save the % (and its source) and reprice. The saved % is a dependency too: if another till
  // changed it, this page's % is written back (last edit wins) rather than leaving Complete
  // waiting for a sync that never comes.
  useEffect(() => {
    if (!currentTrade) return

    if (bulkApplyTimeoutRef.current) {
      clearTimeout(bulkApplyTimeoutRef.current)
    }

    if (bulkCustomerPercent === '' || !Number.isFinite(Number(bulkCustomerPercent))) {
      bulkPercentAbortRef.current?.abort()
      // Cleared: forget the % saved on the draft too, or a reload would bring it back — and
      // remember that staff cleared it, so a reload doesn't fill in the store rule again.
      const saved = currentTradeRef.current
      if (bulkCustomerPercent !== '') return
      if (readTradeCustomerPercent(saved) === null && savedPercentSource(saved) === percentSource) return
    }

    const pct = bulkCustomerPercent === '' ? null : Math.max(0, Math.min(1000, Number(bulkCustomerPercent)))
    bulkApplyTimeoutRef.current = setTimeout(() => {
      const runId = ++bulkApplyRunIdRef.current
      bulkPercentAbortRef.current?.abort()
      const ac = new AbortController()
      bulkPercentAbortRef.current = ac

      void (async () => {
        setBulkUpdating(true)
        setError(null)
        try {
          await applyCustomerPercentToAll(pct, percentSource, ac.signal)
        } catch (err: unknown) {
          if (axios.isCancel(err)) return
          const anyErr = err as { response?: { status?: number; data?: { message?: string } } }
          reportError(err, { page: 'CreateTransaction', action: 'bulkCustomerPercent' })
          // A dropped connection or a server error is worth another go; a 4xx won't change.
          const status = anyErr.response?.status
          const retryable = status === undefined || status >= 500
          const message = anyErr.response?.data?.message || 'Failed to update customer %'
          setError(retryable ? `${message} — retrying…` : message)
          if (retryable && runId === bulkApplyRunIdRef.current) {
            setTimeout(() => {
              if (runId === bulkApplyRunIdRef.current) setPercentSaveRetry((n) => n + 1)
            }, PERCENT_SAVE_RETRY_MS)
          }
        } finally {
          if (runId === bulkApplyRunIdRef.current) {
            setBulkUpdating(false)
          }
        }
      })()
    }, 200)

    return () => {
      if (bulkApplyTimeoutRef.current) {
        clearTimeout(bulkApplyTimeoutRef.current)
      }
    }
  }, [bulkCustomerPercent, percentSource, currentTrade?.id, savedTradePercent, applyCustomerPercentToAll, percentSaveRetry])

  // An open customer card that follows the global % shows the value it will be added at, even when
  // the % arrives after the modal opened (restored with the trade on load).
  const addingCustomerCard = addingItem?.side === 'customer'
  useEffect(() => {
    if (!addingCustomerCard || priceIsOverride || bulkCustomerPercent === '' || marketValueCents <= 0) return
    setItemValueCents(priceFollowingCustomerLine(marketValueCents, bulkCustomerPercent, bulkFloorUsd).unitValueCents)
  }, [addingCustomerCard, priceIsOverride, bulkCustomerPercent, marketValueCents, bulkFloorUsd])

  // The trade's % is saved on the draft and every line follows it; completing before a change
  // (a typed % or a payment-method switch) has reached the server would ring up the old offer.
  const offerSynced = !currentTrade || savedTradePercent === normalizePercent(bulkCustomerPercent)
  const offerUpdating = bulkUpdating || !offerSynced

  // ── Another device on this draft (a phone scanning cards into it, Phase B3) ──
  // The page polls the trade's version while visible and takes the server's copy when it moved on.
  // Staff's own edits come back with the trade they produced, so only the other device's are news.
  // Never applied mid-edit: a quantity still saving, the % being saved, a price editor or the
  // add-card modal open, a basket or customer save, or the sale completing; it lands once idle.
  const [openPriceEditors, setOpenPriceEditors] = useState(0)
  const onPriceEditing = useCallback((editing: boolean) => setOpenPriceEditors((n) => Math.max(0, n + (editing ? 1 : -1))), [])
  const [remoteNote, setRemoteNote] = useState<{ text: string; ids: ReadonlySet<string> } | null>(null)
  const localEditInFlight = qtyEditsPending || offerUpdating || openPriceEditors > 0 || Boolean(addingItem)
    || loading || showPaymentModal || committingBasket || savingCustomerDetails || cancelTradeBusy
  const applyRemoteTrade = useCallback((trade: TradeType & TradeStockNotices) => {
    const prev = currentTradeRef.current
    if (!prev || prev.id !== trade.id) return
    const fresh = newCustomerLineIds(prev, trade)
    const next = fromTradeApiResponse(trade)
    currentTradeRef.current = next
    setCurrentTrade(next)
    if (trade.status !== 'draft') {
      setWarning(`This transaction was ${trade.status === 'completed' ? 'completed' : 'cancelled'} on another device. Start a new one to carry on.`)
      return
    }
    setWarning(tradeStockNotice(trade))
    if (fresh.length) {
      const copies = (next.customerItems ?? []).filter((i) => fresh.includes(i.id)).reduce((n, i) => n + i.quantity, 0)
      setRemoteNote({ text: `Updated from phone · ${copies} new card${copies === 1 ? '' : 's'}`, ids: new Set(fresh) })
    }
  }, [])
  const onRemoteTradeMissing = useCallback(() => {
    setWarning('This transaction no longer exists. It may have been deleted on another device.')
  }, [])
  useTradeAutoRefresh<TradeType & TradeStockNotices>({
    tradeId: currentTrade?.status === 'draft' ? currentTrade.id : null,
    known: currentTrade,
    busy: localEditInFlight,
    onTrade: applyRemoteTrade,
    onMissing: onRemoteTradeMissing,
  })
  useEffect(() => {
    if (!remoteNote) return
    const t = setTimeout(() => setRemoteNote(null), 5000)
    return () => clearTimeout(t)
  }, [remoteNote])

  // "Check printing" on customer lines: another printing of the card is priced quite differently
  // (a flag only — it never stops Complete). Tapping it shows the printings and their prices.
  const printingChecks = usePrintingChecks(currentTrade?.customerItems)

  // Update item quantity: on screen at once, sent through the per-trade queue above.
  const updateItemQuantity = (itemId: string, newQuantity: number) => {
    const trade = currentTrade
    const queue = qtyQueueRef.current
    if (!trade || !queue || newQuantity < 1) return
    const item = trade.items?.find((i) => i.id === itemId)
    if (!item) return

    qtyQueueTradeIdRef.current = trade.id
    setError(null)
    setCurrentTrade((prev) => (prev && prev.id === trade.id ? optimisticQuantityChange(prev, itemId, newQuantity) : prev))
    queue.set(itemId, newQuantity, item.quantity)
  }

  // Reprice one line in place (inline editor). Resolves false on failure so the editor stays open.
  const updateItemPrice = async (itemId: string, update: LinePriceUpdate): Promise<boolean> => {
    const trade = currentTrade
    if (!trade) return false
    setError(null)
    try {
      const response = await api.patch(`/trades/${trade.id}/items/${itemId}`, update)
      const updatedTrade = (response.data?.data?.trade ?? null) as (TradeType & TradeStockNotices) | null
      if (updatedTrade && currentTradeRef.current?.id === trade.id) {
        setCurrentTrade(fromTradeApiResponse(updatedTrade))
        setWarning(tradeStockNotice(updatedTrade))
      }
      return true
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string; error?: string } } }
      reportError(err, { page: 'CreateTransaction', action: 'updateItemPrice' })
      setError(anyErr.response?.data?.message || anyErr.response?.data?.error || 'Failed to update price')
      return false
    }
  }

  /** The popup closed: what it scanned is on the trade, so show it now rather than at the next poll. */
  const closeScanPopup = useCallback(async () => {
    const tradeId = scanPopup?.tradeId
    setScanPopup(null)
    if (!tradeId) return
    try {
      const res = await api.get(`/trades/${tradeId}`)
      if (res.data?.data) setCurrentTrade(fromTradeApiResponse(res.data.data))
    } catch { /* the version poll catches up */ }
  }, [scanPopup])

  /** A device with a camera in hand scans here, in a popup over the trade; a till shows the code for a phone. */
  const scanIntoSide = async (side: 'store' | 'customer') => {
    if (!window.matchMedia('(max-width: 1024px), (pointer: coarse)').matches) {
      setPhonePairingSide(side)
      return
    }
    try {
      const trade = await ensureTrade()
      setScanPopup({ tradeId: trade.id, side })
    } catch (err: unknown) {
      reportError(err, { page: 'CreateTransaction', action: 'scanIntoSide' })
      setError("Couldn't start the transaction. Try again.")
    }
  }

  /** One write on the server, repriced for the side it lands on; a reason comes back if it can't go. */
  const moveItem = async (itemId: string, name: string, toSide: 'store' | 'customer'): Promise<boolean> => {
    const trade = currentTrade
    if (!trade || movingItemId) return false
    setError(null)
    setMovingItemId(itemId)
    try {
      const response = await api.post(`/trades/${trade.id}/items/${itemId}/move`, { toSide })
      const updatedTrade = (response.data?.data?.trade ?? null) as (TradeType & TradeStockNotices) | null
      if (updatedTrade && currentTradeRef.current?.id === trade.id) {
        setCurrentTrade(fromTradeApiResponse(updatedTrade))
        setWarning(tradeStockNotice(updatedTrade))
      }
      const value = Number(response.data?.data?.item?.totalValueCents ?? 0)
      setLastMove({ itemId, name, toSide, valueCents: value })
      return true
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string; error?: string } } }
      reportError(err, { page: 'CreateTransaction', action: 'moveItem' })
      setError(anyErr.response?.data?.message || anyErr.response?.data?.error || "Couldn't move that card.")
      return false
    } finally {
      setMovingItemId(null)
    }
  }

  /** Correct a line's printing or condition, or replace its card, in place; the server reprices it. */
  const changeItemVariant = async (itemId: string, variant: LineVariant): Promise<boolean> => {
    const trade = currentTrade
    if (!trade) return false
    setError(null)
    try {
      const response = await api.post(`/trades/${trade.id}/items/${itemId}/variant`, variant)
      const updatedTrade = (response.data?.data?.trade ?? null) as (TradeType & TradeStockNotices) | null
      if (updatedTrade && currentTradeRef.current?.id === trade.id) {
        setCurrentTrade(fromTradeApiResponse(updatedTrade))
        setWarning(tradeStockNotice(updatedTrade))
      }
      return true
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string; error?: string } } }
      reportError(err, { page: 'CreateTransaction', action: 'changeItemVariant' })
      setError(anyErr.response?.data?.message || anyErr.response?.data?.error || "Couldn't change that card.")
      return false
    }
  }

  const undoLastMove = async () => {
    if (!lastMove) return
    const back = lastMove.toSide === 'store' ? 'customer' : 'store'
    if (await moveItem(lastMove.itemId, lastMove.name, back)) setLastMove(null)
  }

  // Opened from the scanner or a link for one side: a phone stacks the sides, so go to it (once).
  const scrolledToSideRef = useRef(false)
  const hasTradeLoaded = Boolean(currentTrade)
  useEffect(() => {
    if (scrolledToSideRef.current || !hasTradeLoaded || (sideParam !== 'store' && sideParam !== 'customer')) return
    scrolledToSideRef.current = true
    document.getElementById(`trade-side-${sideParam}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [hasTradeLoaded, sideParam])

  const decrementOrRemoveItem = async (item: TradeItem) => {
    if (item.quantity <= 1) {
      await removeItem(item.id)
      return
    }
    updateItemQuantity(item.id, item.quantity - 1)
  }

  // Remove item from trade
  const removeItem = async (itemId: string) => {
    if (!currentTrade) return

    const prev = currentTrade
    const removed = prev.items?.find((i) => i.id === itemId)
    setCurrentTrade(optimisticRemoveItem(currentTrade, itemId))

    try {
      const response = await api.delete(`/trades/${currentTrade.id}/items/${itemId}`)
      const updatedTrade = response.data.data.trade
      const items = updatedTrade.items || []
      setCurrentTrade({
        ...updatedTrade,
        items,
        storeItems: items.filter((item: TradeItem) => item.side === 'store'),
        customerItems: items.filter((item: TradeItem) => item.side === 'customer'),
      })
      if (removed) {
        setUndoRemoveStack((s) => [removed, ...s].slice(0, UNDO_REMOVE_STACK_MAX))
        if (undoRemoveTimerRef.current) clearTimeout(undoRemoveTimerRef.current)
        undoRemoveTimerRef.current = setTimeout(() => setUndoRemoveStack([]), 12_000)
      }
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'removeItem' })
      setCurrentTrade(prev)
      setError(err.response?.data?.message || 'Failed to remove item')
    }
  }

  const undoLastRemove = async () => {
    const line = undoRemoveStack[0]
    if (!line || !currentTrade) return
    setError(null)
    try {
      await api.post(`/trades/${currentTrade.id}/items`, {
        items: [tradeItemToAddPayload(line)],
      })
      const refreshed = await api.get(`/trades/${currentTrade.id}`)
      const t = refreshed.data?.data as TradeType | undefined
      if (t) {
        const items = t.items || []
        setCurrentTrade({
          ...t,
          items,
          storeItems: items.filter((item: TradeItem) => item.side === 'store'),
          customerItems: items.filter((item: TradeItem) => item.side === 'customer'),
        })
      }
      setUndoRemoveStack((s) => {
        const next = s.slice(1)
        if (undoRemoveTimerRef.current) {
          clearTimeout(undoRemoveTimerRef.current)
          undoRemoveTimerRef.current = null
        }
        if (next.length > 0) {
          undoRemoveTimerRef.current = setTimeout(() => setUndoRemoveStack([]), 12_000)
        }
        return next
      })
      setSuccess('Line restored')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'undoRemoveItem' })
      setError(err.response?.data?.message || 'Could not undo remove')
    }
  }

  // Complete the trade
  const completeTrade = async () => {
    if (!currentTrade) return

    const hasItems = (currentTrade.storeItems?.length || 0) > 0 || (currentTrade.customerItems?.length || 0) > 0

    if (!hasItems) {
      setError('Add at least one item before completing this transaction')
      return
    }
    if (qtyQueueRef.current?.pending) {
      setError('Wait a moment — a quantity change is still saving.')
      return
    }
    if (offerUpdating) {
      setError('Wait a moment — the customer offer is still updating.')
      return
    }
    
    setLoading(true)
    setError(null)
    // Any stock advisory is about to be settled one way or the other: either the sale goes
    // through, or the failure comes back as a specific error. Leaving the old banner up would
    // contradict whichever answer arrives.
    setWarning(null)

    try {
      const completePayload = buildCompleteTradePayload(
        currentTrade.differenceValueCents,
        selectedPaymentMethod,
      )

      // One key per trade: if this request times out but went through, pressing Complete again
      // replays the sale's answer instead of hitting "already completed" (or worse).
      const res = await api.post(
        `/trades/${currentTrade.id}/complete`,
        completePayload,
        withIdempotencyKey(`trade-complete:${currentTrade.id}`),
      )
      const completed = res.data?.data as TradeResponse | undefined

      if (completed) {
        setReceiptNumberLoading(false)
        setCompletedTradeData(mapTradeToCompletedData(completed))
      } else {
        setReceiptNumberLoading(true)
        setCompletedTradeData({
          id: currentTrade.id,
          type: currentTrade.type,
          receiptNumber: null,
          customerName: currentTrade.customerName || customerName,
          customerEmail: currentTrade.customerEmail || customerEmail,
          customerPhone: currentTrade.customerPhone || customerPhone,
          storeValueCents: currentTrade.storeValueCents,
          customerValueCents: currentTrade.customerValueCents,
          differenceValueCents: currentTrade.differenceValueCents,
          storeItems: currentTrade.storeItems || [],
          customerItems: currentTrade.customerItems || [],
          paymentMethod: selectedPaymentMethod,
          completedAt: new Date().toISOString(),
          notes: currentTrade.notes || notes,
        })
        try {
          const refreshed = await api.get(`/trades/${currentTrade.id}`)
          const trade = refreshed.data?.data as TradeResponse | undefined
          if (trade?.status === 'completed') {
            setCompletedTradeData(mapTradeToCompletedData(trade))
          }
        } catch (refetchErr: unknown) {
          reportError(refetchErr, { page: 'CreateTransaction', action: 'refetchCompletedTradeReceipt' })
        } finally {
          setReceiptNumberLoading(false)
        }
      }

      setShowPaymentModal(false)
      setCurrentTrade(null)
      // A completed sale moves stock, revenue and the transaction list. Without this, opening
      // Inventory or Transactions within the 30s staleTime showed the pre-sale figures.
      for (const queryKey of [['inventory'], ['transactions'], ['dashboard'], ['sales']]) {
        void queryClient.invalidateQueries({ queryKey })
      }
      trackUserAction('trade_completed', {
        tradeId: currentTrade.id,
        storeItems: currentTrade.storeItems?.length ?? 0,
        customerItems: currentTrade.customerItems?.length ?? 0,
      })
      if (orgId) {
        localStorage.removeItem(activeTradeStorageKey(orgId))
      }
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'completeTrade' })
      setError(err.response?.data?.message || 'Failed to complete transaction')
    } finally {
      setLoading(false)
    }
  }

  const startFreshTrade = () => {
    // Local-only reset — do NOT create a DB row. The next real action (item,
    // customer info, basket, scan) lazily creates the new trade. If the current
    // draft was created but left empty (e.g. item added then removed), clean it
    // up so it doesn't linger in history.
    const previous = currentTradeRef.current
    if (previous && shouldDeleteDraftTradeRef.current(previous)) {
      // ifEmpty: a phone may have scanned cards onto it since this page last looked
      void api.delete(`/trades/${previous.id}`, { params: { ifEmpty: 1 } }).catch(() => undefined)
    }

    setCurrentTrade(null)
    currentTradeRef.current = null
    setAddingItem(null)
    setCustomerName('')
    setCustomerEmail('')
    setCustomerPhone('')
    setNotes('')
    setShowCustomerDetails(false)
    setLoadedBasket(null)
    setError(null)
    if (orgId) {
      localStorage.removeItem(activeTradeStorageKey(orgId))
    }
    setSuccess('New transaction started')
    setTimeout(() => setSuccess(null), 1500)
  }

  // Start a new trade after viewing a receipt — returns to an empty in-memory
  // builder. No DB row is created until the next real action.
  const startNewTrade = () => {
    setCompletedTradeData(null)
    setReceiptNumberLoading(false)
    setCurrentTrade(null)
    currentTradeRef.current = null
    setCustomerName('')
    setCustomerEmail('')
    setCustomerPhone('')
    setNotes('')
    setSuccess(null)
    setError(null)
  }

  const draftCleanupMountGenRef = useRef(0)

  useEffect(() => {
    draftCleanupMountGenRef.current += 1
    const genAtMount = draftCleanupMountGenRef.current

    if (pendingDraftCleanupRef.current) {
      clearTimeout(pendingDraftCleanupRef.current)
      pendingDraftCleanupRef.current = null
    }

    return () => {
      const tradeToCleanup = currentTradeRef.current
      if (!tradeToCleanup || !shouldDeleteDraftTradeRef.current(tradeToCleanup)) return

      const id = tradeToCleanup.id
      pendingDraftCleanupRef.current = setTimeout(() => {
        pendingDraftCleanupRef.current = null
        if (draftCleanupMountGenRef.current !== genAtMount) return
        void api.delete(`/trades/${id}`, { params: { ifEmpty: 1 } }).catch(() => {
          // best-effort cleanup for truly empty draft trades only
        })
      }, 600)
    }
  }, [])

  const openReceiptPdf = useCallback(
    async (paperWidthMm: 58 | 80) => {
      if (!completedTradeData) return
      const { generateThermalReceiptPdf } = await import('../../utils/pdfReceipt')
      generateThermalReceiptPdf({
        trade: {
          ...completedTradeData,
          storeName: user?.organizationName || 'TCGEasy Store',
          staffName: user?.displayName || user?.firstName || 'Staff',
        },
        paperWidthMm,
        locale: orgLocale,
        formatPrice,
        onError: (err: unknown) => {
          if (import.meta.env.DEV) {
            console.error('Failed to generate PDF:', err)
          }
          reportError(err, { page: 'CreateTransaction', action: 'thermalReceiptPdf' })
          setError(createTransactionCopy.receiptPdfFailed)
        },
      })
    },
    [completedTradeData, user, orgLocale, formatPrice],
  )

  const handlePrintReceipt = () => {
    void openReceiptPdf(80)
  }

  const handlePrintThermalReceipt = () => {
    void openReceiptPdf(58)
  }

  // Email the trade receipt (resend) — same routing as completion: merchant inbox + customer when on file
  const handleEmailReceipt = async () => {
    if (!completedTradeData) return
    try {
      await api.post(`/trades/${completedTradeData.id}/send-receipt`)
      const customer = completedTradeData.customerEmail?.trim()
      setSuccess(
        customer
          ? 'Receipt emailed to the store and to the customer address on file.'
          : 'Receipt emailed to the store.'
      )
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'sendReceiptEmail' })
      setError(err.response?.data?.message || 'Failed to send receipt email')
    }
  }

  const confirmCancelTrade = async () => {
    // No server-side trade yet (lazy creation) — just clear the in-memory session.
    if (!currentTrade) {
      currentTradeRef.current = null
      setCustomerName('')
      setCustomerEmail('')
      setCustomerPhone('')
      setNotes('')
      setLoadedBasket(null)
      setCancelTradeConfirmOpen(false)
      return
    }

    setCancelTradeBusy(true)
    setError(null)

    try {
      await api.post(`/trades/${currentTrade.id}/cancel`)
      setCurrentTrade(null)
      if (orgId) {
        localStorage.removeItem(activeTradeStorageKey(orgId))
      }
      setCustomerName('')
      setCustomerEmail('')
      setCustomerPhone('')
      setNotes('')
      setLoadedBasket(null)
      setCancelTradeConfirmOpen(false)
      setSuccess('Transaction cancelled')
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      reportError(err, { page: 'CreateTransaction', action: 'cancelTransaction' })
      setError(err.response?.data?.message || 'Failed to cancel transaction')
    } finally {
      setCancelTradeBusy(false)
    }
  }

  // Calculate difference display
  const getDifferenceDisplay = () => {
    if (!currentTrade) return null
    
    const diff = currentTrade.differenceValueCents
    if (diff === 0) {
      return <span className={styles.even}>Even</span>
    } else if (diff > 0) {
      return <span className={styles.customerOwes}>Customer owes {formatPrice(diff)}</span>
    } else {
      return <span className={styles.storeOwes}>Store owes {formatPrice(Math.abs(diff))}</span>
    }
  }

  // Render the active trade builder
  const renderTradeBuilder = () => {
    const inferredTradeType = currentTrade?.type || 'trade'
    // the server calls a deal with only the customer's cards a buy, but for store credit it's a trade-in
    const tradeLabel = inferredTradeType === 'buy' && selectedPaymentMethod === 'store_credit' ? 'Trade-in' : getTradeTypeLabel(inferredTradeType)
    const countOf = (items?: Array<{ quantity: number }>) => (items || []).reduce((n, it) => n + it.quantity, 0)
    const storeCount = countOf(currentTrade?.storeItems)
    const customerCount = countOf(currentTrade?.customerItems)
    const lineProps = (item: TradeItem) => {
      const imageSrc = getTradeItemImage(item)
      return {
        item,
        imageSrc: imageSrc && !tradeImageLoadFailedIds.has(item.id) ? imageSrc : null,
        onImageError: () => setTradeImageLoadFailedIds((prev) => new Set(prev).add(item.id)),
        formatPrice,
        globalCustomerPercent: bulkCustomerPercent,
        onSavePrice: (update: LinePriceUpdate) => updateItemPrice(item.id, update),
        onPriceEditing,
        onDecrease: () => void decrementOrRemoveItem(item),
        onIncrease: () => updateItemQuantity(item.id, item.quantity + 1),
        moveDisabled: Boolean(movingItemId) || qtyEditsPending,
        onRemove: () => removeItem(item.id),
        onChangeVariant: (variant: LineVariant) => changeItemVariant(item.id, variant),
      }
    }
    const sideHead = (side: 'store' | 'customer', title: string, count: number, totalCents: number) => (
      <div className={styles.sideHead}>
        <h3>{title} <span className={styles.sideCount}>{count}</span></h3>
        <span className={styles.total}>{formatPrice(totalCents)}</span>
        <div className={styles.sideActions}>
          <button type="button" className={styles.sideScanBtn} onClick={() => void scanIntoSide(side)}>
            <ScanLine size={16} aria-hidden /> Scan
          </button>
          <button type="button" className={styles.sideSearchToggle} aria-expanded={phoneSearch[side]}
            onClick={() => setPhoneSearch((prev) => ({ ...prev, [side]: !prev[side] }))}>
            <Search size={16} aria-hidden /> Search
          </button>
        </div>
      </div>
    )
    const canComplete =
      ((currentTrade?.storeItems?.length || 0) > 0 || (currentTrade?.customerItems?.length || 0) > 0) &&
      !qtyEditsPending &&
      !offerUpdating

    return (
    <div className={styles.tradeBuilder}>
      <section className={styles.dealBar} aria-label="This deal">
        <div className={styles.dealTop}>
          <span className={styles.tradeType}>
            {inferredTradeType === 'trade' && <ArrowLeftRight size={18} />}
            {inferredTradeType === 'buy' && <Package size={18} />}
            {inferredTradeType === 'sell' && <DollarSign size={18} />}
            {tradeLabel}
          </span>
          {currentTrade?.id ? (
            <span className={styles.tradeId}>{formatActiveTradeRefLabel(currentTrade)}</span>
          ) : (
            <span className={styles.unsavedChip} title="Nothing is recorded until you add an item, customer, or basket — empty sessions are never saved to history.">
              Not saved yet
            </span>
          )}
          <button type="button" className={styles.customerToggle} onClick={() => setShowCustomerDetails((v) => !v)}
            title={hasFilledCustomerInfo ? customerSummary || 'Customer details saved' : undefined}>
            <User size={14} aria-hidden />
            {currentTrade?.customerName ? currentTrade.customerName : 'Add customer'}
          </button>
          <div ref={dealMenuRef} className={styles.dealMenu}>
            <button type="button" className={styles.dealMenuBtn} aria-haspopup="menu" aria-expanded={dealMenuOpen} aria-label="More actions" onClick={() => setDealMenuOpen((v) => !v)}>
              <MoreHorizontal size={18} aria-hidden />
            </button>
            {dealMenuOpen && (
              <div className={styles.dealMenuList} role="menu">
                <button type="button" role="menuitem" onClick={() => { setDealMenuOpen(false); setShowCustomerView(true) }}><Eye size={16} aria-hidden /> Customer view or print</button>
                <button type="button" role="menuitem" onClick={() => { setDealMenuOpen(false); setShowQrScanner(true) }}><QrCode size={16} aria-hidden /> Scan customer QR</button>
                <button type="button" role="menuitem" onClick={() => { setDealMenuOpen(false); setPhonePairingSide('customer') }}><Smartphone size={16} aria-hidden /> Scan with a phone</button>
                <button type="button" role="menuitem" onClick={() => { setDealMenuOpen(false); setShowLoadBasketModal(true) }}><Package size={16} aria-hidden /> Add basket</button>
                <button type="button" role="menuitem" onClick={() => { setDealMenuOpen(false); startFreshTrade() }}><Plus size={16} aria-hidden /> New transaction</button>
                <button type="button" role="menuitem" className={styles.dealMenuDanger} onClick={() => { setDealMenuOpen(false); setCancelTradeConfirmOpen(true) }}><X size={16} aria-hidden /> Cancel transaction</button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.balance}>
          <div className={styles.balanceTile}>
            <span>Customer gives</span>
            <b>{formatPrice(currentTrade?.customerValueCents || 0)}</b>
          </div>
          <div className={styles.balanceTile}>
            <span>Customer takes</span>
            <b>{formatPrice(currentTrade?.storeValueCents || 0)}</b>
          </div>
        </div>

        <div className={styles.bulkPercentRow} title="Set a default market percentage for customer cards. For example, 70% means customer cards are valued at 70% of market price.">
          <div className={styles.bulkPercentLabelWrapper}>
            <span className={styles.bulkPercentLabel}>Customer % of Market</span>
            <Info size={14} className={styles.bulkPercentInfoIcon} />
          </div>
          <div className={styles.bulkPercentControls}>
            <input
              type="number"
              value={bulkCustomerPercent === '' ? '' : bulkCustomerPercent}
              onChange={(e) => {
                const raw = e.target.value
                if (raw === '') {
                  setPercentSource('manual')
                  setBulkCustomerPercent('')
                  return
                }
                const num = Number(raw)
                if (num < 0 || num > CUSTOMER_PERCENT_UI_MAX) return
                setPercentSource('manual')
                setBulkCustomerPercent(num)
              }}
              placeholder="100"
              min={0}
              max={CUSTOMER_PERCENT_UI_MAX}
              step={1}
              className={styles.bulkPercentInput}
            />
            <span className={styles.bulkPercentSuffix}>%</span>
          </div>
          <span className={styles.bulkPercentHint} aria-live="polite">
            {bulkUpdating ? 'Updating prices…' : 'Prices every customer card. Tap a price to set one by hand.'}
          </span>
          <div className={styles.paymentRuleRow}>
            <div className={styles.paymentRuleChips} role="group" aria-label="Paying the customer in">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  aria-pressed={selectedPaymentMethod === method.value}
                  className={selectedPaymentMethod === method.value ? styles.paymentRuleChipActive : styles.paymentRuleChip}
                  onClick={() => setSelectedPaymentMethod(method.value)}
                >
                  {method.label}
                </button>
              ))}
            </div>
            <span className={styles.bulkPercentHint}>
              {percentSource === 'rules'
                ? rulePercent === undefined
                  ? 'Loading store rules…'
                  : rulePercent === null
                    ? 'No store rule, so cards go in at full market. Type a % to change that.'
                    : `Store rule: ${rulePercent}% for ${selectedPaymentMethod === 'store_credit' ? 'store credit' : 'cash'}`
                : 'Set for this trade'}
            </span>
            {percentSource === 'manual' && typeof rulePercent === 'number' && (
              <button type="button" className={styles.useRuleBtn} onClick={() => setPercentSource('rules')}>
                Use store rule ({rulePercent}%)
              </button>
            )}
          </div>
        </div>
      </section>

      {loadedBasket && (
        <div className={styles.basketQuickSummary}>
          <div className={styles.basketQuickSummaryMain}>
            <span className={styles.basketQuickCode}>Using basket ID: {loadedBasket.code}</span>
            <span>{loadedBasket.itemCount} card{loadedBasket.itemCount !== 1 ? 's' : ''} added to Store Items · {formatLoadedBasketTotal(loadedBasket)}</span>
            {loadedBasket.customerSummary && (
              <span>👤 {loadedBasket.customerSummary}</span>
            )}
          </div>
          <div className={styles.basketQuickControls}>
            <button
              type="button"
              className={styles.newTradeBtn}
              onClick={() => setShowLoadBasketModal(true)}
            >
              View Basket
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => void removeLoadedBasket()}
              disabled={loading}
            >
              {loading ? 'Removing…' : 'Remove Basket'}
            </button>
          </div>
        </div>
      )}

      {showCustomerDetails && (
        <CustomerPanel
          customerName={customerName}
          customerEmail={customerEmail}
          customerPhone={customerPhone}
          notes={notes}
          onCustomerFieldChange={handleCustomerFieldChange}
          onCustomerFieldFocus={handleCustomerFieldFocus}
          onSuggestionKeyDown={handleSuggestionKeyDown}
          onNotesChange={setNotes}
          onClose={() => setShowCustomerDetails(false)}
          onSave={() => void saveCustomerDetails()}
          savingCustomerDetails={savingCustomerDetails}
          shouldShowCustomerSuggestionPanel={shouldShowCustomerSuggestionPanel}
          filteredCustomerSuggestions={filteredCustomerSuggestions}
          selectCustomerSuggestion={selectCustomerSuggestion}
          customerSearchLoading={customerSearchLoading}
          customerSearchError={customerSearchError}
          activeSuggestion={activeSuggestion}
          setActiveSuggestion={setActiveSuggestion}
          customerNameRef={customerNameRef}
          customerEmailRef={customerEmailRef}
          customerPhoneRef={customerPhoneRef}
          suggestionsRef={suggestionsRef}
        />
      )}

      {/* Both sides: side by side on a wide screen, one list on a phone. Customer first: gives, then takes. */}
      <div className={styles.tradeColumns}>
        <div id="trade-side-customer" className={`${styles.column} ${styles.customerColumn}`}>
          {sideHead('customer', "Customer's cards", customerCount, currentTrade?.customerValueCents || 0)}
          <div className={`${styles.tradeSearchWrapper} ${phoneSearch.customer ? styles.sideSearchOpen : ''}`}>
            <UnifiedCardSearch
              onSelectCard={selectCustomerCard}
              placeholder="Search all cards"
              clearQueryOnSelect={false}
              showGameFilter={true}
              showSetFilter={true}
              defaultSearchBy="cards"
              showSetNumberInput={false}
              className={styles.tradeSearch}
              resultsSource="trade"
              resultsTradeId={currentTrade?.id || tradeIdParam || undefined}
              resultsTradeSide="customer"
              catalogVariantPreview="count"
            />
          </div>
          <div className={styles.itemsList}>
            {(currentTrade?.customerItems || []).map((item) => {
              const printingCheck = printingChecks.get(item.id)
              return (
                <TradeLineRow
                  key={item.id}
                  {...lineProps(item)}
                  bulkFloorUsd={bulkFloorUsd}
                  priceDisabled={qtyEditsPending || bulkUpdating}
                  atMax={false}
                  onMove={() => void moveItem(item.id, item.itemName, 'store')}
                  fresh={remoteNote?.ids.has(item.id)}
                  printingNote={printingCheck ? printingCheckText(printingCheck, formatPrice) : null}
                />
              )
            })}
            {customerCount === 0 && <div className={styles.emptyState}>Scan the cards the customer is handing over</div>}
          </div>
        </div>

        <div id="trade-side-store" className={`${styles.column} ${styles.storeColumn}`}>
          {sideHead('store', "Store's cards", storeCount, currentTrade?.storeValueCents || 0)}
          <div className={`${styles.tradeSearchWrapper} ${phoneSearch.store ? styles.sideSearchOpen : ''}`}>
            <UnifiedCardSearch
              onSelectCard={selectStoreItem}
              placeholder="Search your inventory"
              searchType="inventory"
              clearQueryOnSelect={false}
              showGameFilter={true}
              showSetFilter={true}
              showSetNumberInput={false}
              className={styles.tradeSearch}
            />
          </div>
          <div className={styles.itemsList}>
            {(currentTrade?.storeItems || []).map((item) => {
              const maxQty = item.product?.availableQuantity ?? item.product?.quantity ?? undefined
              return (
                <TradeLineRow
                  key={item.id}
                  {...lineProps(item)}
                  priceDisabled={qtyEditsPending}
                  atMax={typeof maxQty === 'number' && maxQty >= 0 && item.quantity >= maxQty}
                  onMove={() => void moveItem(item.id, item.itemName, 'customer')}
                />
              )
            })}
            {storeCount === 0 && <div className={styles.emptyState}>Scan or search your cards the customer is taking</div>}
          </div>
        </div>
      </div>

      {/* Sticky Summary Banner */}
      <div className={styles.stickySummaryBanner}>
        <div className={styles.summaryBannerContent}>
          <div className={styles.summaryBannerLeft}>
            <span className={styles.summaryBannerLabel}>Transaction Total</span>
            <div className={styles.diffValue}>
              {getDifferenceDisplay()}
            </div>
          </div>
          <div className={styles.tradeActions}>
            <button
              className={styles.completeBtn}
              onClick={() => setShowPaymentModal(true)}
              disabled={!canComplete}
              title={qtyEditsPending ? 'Saving quantity changes…' : undefined}
            >
              <Check size={20} />
              Complete<span className={styles.completeBtnLong}> Transaction</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    )
  }

  const handleQrScannerScan = useCallback(
    (productId: string) => addScannedProductToTrade(productId, { suppressNavigation: true }),
    [addScannedProductToTrade],
  )

  if (!canAccessTradeCenter) {
    return (
      <div className={styles.container}>
        <PageHeader
          title="Transaction Center"
          subtitle="Manage swaps, buys, and sells with customers"
        />
        <UpgradePrompt capability="tradeCenter" source="trade-create" />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <PageHeader
        title="Transaction Center"
        actions={
          <div className={styles.actionButtons}>
            <Link to="/analytics/in-store" className={styles.startTradeLink}>
              <BarChart3 size={18} />
              Transaction Analytics
            </Link>
            <Link to="/transactions" className={styles.historyLink}>
              <History size={18} />
              Transaction History
            </Link>
          </div>
        }
      />

      {/* Toasts — fixed stack so feedback never pushes the builder around */}
      <div className={styles.toastStack} aria-live="polite">
        {error && (
          <div className={styles.alert + ' ' + styles.error} role="alert" aria-live="assertive">
            <AlertCircle size={16} aria-hidden />
            {error}
            <button type="button" aria-label="Dismiss error" onClick={() => setError(null)}>
              <X size={14} aria-hidden />
            </button>
          </div>
        )}

        {warning && (
          <div className={styles.alert + ' ' + styles.warning} role="status" aria-live="polite">
            <AlertTriangle size={16} aria-hidden />
            {warning}
            <button type="button" aria-label="Dismiss warning" onClick={() => setWarning(null)}>
              <X size={14} aria-hidden />
            </button>
          </div>
        )}

        {success && (
          <div className={styles.alert + ' ' + styles.success} role="status" aria-live="polite">
            <Check size={16} aria-hidden />
            {success}
          </div>
        )}

        {remoteNote && !completedTradeData && (
          <div className={styles.alert + ' ' + styles.success} role="status" aria-live="polite">
            <Smartphone size={16} aria-hidden />
            {remoteNote.text}
          </div>
        )}

        {lastMove && !completedTradeData && (
          <div className={styles.alert + ' ' + styles.success} role="status">
            <span>
              Moved {lastMove.name} to the {lastMove.toSide === 'store' ? "store's" : "customer's"} cards at {formatPrice(lastMove.valueCents)}.{' '}
              <button type="button" disabled={Boolean(movingItemId)} onClick={() => void undoLastMove()}>Undo</button>
              {' · '}
              <button type="button" onClick={() => setLastMove(null)}>Dismiss</button>
            </span>
          </div>
        )}

        {undoRemoveStack.length > 0 && !completedTradeData && (
          <div className={styles.alert + ' ' + styles.success} role="status">
            <span>
              Removed {undoRemoveStack[0].itemName}
              {undoRemoveStack.length > 1 ? ` (${undoRemoveStack.length - 1} more to restore)` : ''}.{' '}
              <button type="button" onClick={() => void undoLastRemove()}>
                Undo
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => {
                  if (undoRemoveTimerRef.current) clearTimeout(undoRemoveTimerRef.current)
                  undoRemoveTimerRef.current = null
                  setUndoRemoveStack([])
                }}
              >
                Dismiss
              </button>
            </span>
          </div>
        )}
      </div>

      {/* Main Content */}
      {completedTradeData ? (
        <TradeReceipt
          data={completedTradeData}
          formatPrice={formatPrice}
          receiptLocale={orgLocale}
          storeName={user?.organizationName}
          receiptNumberLoading={receiptNumberLoading}
          getTradeTypeLabel={getTradeTypeLabel}
          onPrint80={handlePrintReceipt}
          onPrint58={handlePrintThermalReceipt}
          onEmail={() => void handleEmailReceipt()}
          onNewTransaction={() => void startNewTrade()}
        />
      ) : (
        renderTradeBuilder()
      )}

      {/* Modals */}
      {addingItem && (
        <AddItemModal
          addingItem={addingItem}
          formatPrice={formatPrice}
          loading={loading}
          itemQuantity={itemQuantity}
          itemValueCents={itemValueCents}
          marketValueCents={marketValueCents}
          overrideTotalValueCents={overrideTotalValueCents}
          itemCondition={itemCondition}
          itemPrinting={itemPrinting}
          selectedVariantId={selectedVariantId}
          priceIsOverride={priceIsOverride}
          globalCustomerPercent={bulkCustomerPercent}
          bulkFloorUsd={bulkFloorUsd}
          onClose={() => setAddingItem(null)}
          onAddToTrade={() => void addItemToTrade()}
          setItemQuantity={setItemQuantity}
          setItemValueCents={setItemValueCents}
          setMarketValueCents={setMarketValueCents}
          setOverrideTotalValueCents={setOverrideTotalValueCents}
          setPriceIsOverride={setPriceIsOverride}
          setItemCondition={setItemCondition}
          setItemPrinting={setItemPrinting}
          setSelectedVariantId={setSelectedVariantId}
          onZoomImage={setZoomedImageUrl}
          onReportIssue={(ctx) => {
            setReportContext(ctx)
            setReportModalOpen(true)
          }}
        />
      )}
      {showPaymentModal && currentTrade && (
        <PaymentModal
          open={showPaymentModal}
          trade={currentTrade}
          formatPrice={formatPrice}
          selectedPaymentMethod={selectedPaymentMethod}
          onSelectPaymentMethod={setSelectedPaymentMethod}
          onClose={() => setShowPaymentModal(false)}
          onComplete={() => void completeTrade()}
          loading={loading}
          offerUpdating={offerUpdating}
        />
      )}
      {showQrScanner && (
        <QrScannerModal
          open
          onClose={() => setShowQrScanner(false)}
          onScanProductId={handleQrScannerScan}
        />
      )}
      {showCustomerView && currentTrade && (
        <CustomerViewSheet trade={currentTrade} storeName={user?.organizationName || 'TCGEasy Store'} formatPrice={formatPrice} onClose={() => setShowCustomerView(false)} />
      )}
      {scanPopup && createPortal(
        <Suspense fallback={null}>
          <CardScannerPopup popup={{ tradeId: scanPopup.tradeId, side: scanPopup.side, onClose: () => void closeScanPopup() }} />
        </Suspense>,
        document.body,
      )}
      {phonePairingSide && (
        <PhonePairingModal open onClose={() => setPhonePairingSide(null)} ensureTradeId={ensurePairingTradeId} side={phonePairingSide} />
      )}
      {showLoadBasketModal && (
        <BasketModal
          open
          onClose={closeLoadBasketModal}
          basketCodePrefix={basketCodePrefix}
          basketCodeInput={basketCodeInput}
          onBasketCodeInputChange={setBasketCodeInput}
          onPreviewBasket={handlePreviewBasket}
          loadingBasket={loadingBasket}
          basketLoadError={basketLoadError}
          basketPreview={basketPreview as BasketPreviewData | null}
          loadedBasket={loadedBasket}
          committingBasket={committingBasket}
          onCommitBasket={handleCommitBasket}
          normalizeBasketCode={normalizeBasketCode}
        />
      )}
      {zoomedImageUrl && (
        <div
          className={styles.imageZoomOverlay}
          onClick={() => setZoomedImageUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Card image"
        >
          <button
            type="button"
            className={styles.imageZoomClose}
            onClick={() => setZoomedImageUrl(null)}
            aria-label="Close image"
          >
            <X size={24} aria-hidden />
          </button>
          {/* Clicking the image (not just the margin) also closes — a full-bleed
              image must never trap the user with no way back to the trade. */}
          <img
            src={zoomedImageUrl}
            alt="Zoomed card"
            className={styles.imageZoomImage}
            onClick={() => setZoomedImageUrl(null)}
          />
        </div>
      )}
      {reportContext && (
        <ReportIssueModal
          isOpen={reportModalOpen}
          onClose={() => { setReportModalOpen(false); setReportContext(null) }}
          context={reportContext}
          formatPrice={formatPrice}
        />
      )}
      <ActionConfirmModal
        isOpen={cancelTradeConfirmOpen}
        title="Cancel transaction?"
        message="This discards the current in-progress transaction. This cannot be undone."
        confirmLabel="Cancel transaction"
        tone="danger"
        isLoading={cancelTradeBusy}
        onCancel={() => setCancelTradeConfirmOpen(false)}
        onConfirm={() => void confirmCancelTrade()}
      />
    </div>
  )
}

export const TransactionCreate = CreateTransaction
export default CreateTransaction
